import { MobileService, MobileServiceFragment, MobileServiceFragmentDoc, Site, SiteVisit } from "@/graphql/__generated__/graphql";
import { Dispatch, JSX, SetStateAction, useState } from "react";
import { CustomToastComponent, TextInfoGroupAlt } from "@/components/shared";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { appendSiteAddress, convertDecimalToBp, formatNumberLocale } from "@/utils/helpers";
import AddSiteVisitComponent from "../../SiteVisits/SiteVisitsComponent";
import { useTranslation } from "react-i18next";
import { useFragment } from "@apollo/client/react";
import { TSiteVisitSchema } from "../../Models";
import { useUpdateSiteVisit } from "@/hooks";
import { toast } from "react-toastify";
import { SubmitAction } from "@/constants/enums";

const EditMobileService = ({ userCanEdit }: { userCanEdit: boolean }): JSX.Element => {

  // Hooks
  const intl = useIntl();
  const { t } = useTranslation(["translation", "common"]);
  const navigate = useNavigate();
  const { tenderID: currentTenderID, serviceID } = useParams();
  const { triggerUpdate, isProcessing } = useUpdateSiteVisit();

  // States
  const [showRemoveSiteVisitAlert, setShowRemoveSiteVisitAlert] = useState<boolean>(false);

  if (!currentTenderID || !serviceID) navigate(-1);

  const { data: service } = useFragment<MobileServiceFragment>({
    fragment: MobileServiceFragmentDoc,
    from: { __typename: "MobileService", id: serviceID },
  });

  const inputFieldsData = [
    { name: "name", label: t("mobileservice.inputs.name.label") },
    { name: "operationalOrganizationShortName", label: t("mobileservice.inputs.operationalorganization.label") },
    { name: "hourlyWage", label: t("mobileservice.inputs.hourlywage.label"), format: "currency" },
    { name: "travelTime", label: t("mobileservice.inputs.traveltime.label"), suffix: " minutes" },
    { name: "nonTariffAllowance", label: t("mobileservice.inputs.nontariffallowance.label"), format: "currency" },
    { name: "hoursBriefingPerEmployee", label: t("mobileservice.inputs.hoursbriefingperemployee.label"), type: "number" },
    { name: "margin", label: t("mobileservice.margin"), format: "percent" },
    { name: "additional", label: t("mobileservice.inputs.additional.label"), format: "boolean" },
  ];

  /**
   * Handler function for `removeSiteVisitMutation`
   */
  const handleRemoveSiteVisit = async (siteVisit: SiteVisit) => {
    try {
      // Create new service with site visit to remove be filtered out
      const newService = {
        ...service,
        siteVisits: service.siteVisits?.filter(visit => visit.id !== siteVisit.id) || []
      };

      // Trigger the site visit update
      await triggerUpdate(newService as MobileService);

      toast(CustomToastComponent, {
        toastId: "site-visit-removed-success",
        data: {
          title: t("toasts:toasts.sitevisitremovedsuccessfully"),
          type: "success",
        }
      });
    }
    catch(error) {
      toast(CustomToastComponent, {
        toastId: "site-visit-removed-error",
        data: {
          title: t("toasts:toasts.somethingwentwrong"),
          content: error.message,
          type: "error",
        }
      });
    }
  };

  /**
   * Handler for the Add Site Visit button if an existing Service is being edited.
   * @param siteVisitData CreateSiteVisitInput!
   */
  const handleAddSiteVisit = async (siteVisitData: TSiteVisitSchema, action: SubmitAction, setShowSiteVisitsModal: Dispatch<SetStateAction<boolean>>) => {
    try {
      const newSiteVisit = {
        margin: convertDecimalToBp(siteVisitData.margin),
        type: siteVisitData.type,
        duration: siteVisitData.duration,
        mondayDay: siteVisitData.mondayDay,
        mondayNight: siteVisitData.mondayNight,
        tuesdayDay: siteVisitData.tuesdayDay,
        tuesdayNight: siteVisitData.tuesdayNight,
        wednesdayDay: siteVisitData.wednesdayDay,
        wednesdayNight: siteVisitData.wednesdayNight,
        thursdayDay: siteVisitData.thursdayDay,
        thursdayNight: siteVisitData.thursdayNight,
        fridayDay: siteVisitData.fridayDay,
        fridayNight: siteVisitData.fridayNight,
        saturdayDay: siteVisitData.saturdayDay,
        saturdayNight: siteVisitData.saturdayNight,
        sundayDay: siteVisitData.sundayDay,
        sundayNight: siteVisitData.sundayNight,
        holidayDay: siteVisitData.holidayDay,
        holidayNight: siteVisitData.holidayNight,
        startDate: siteVisitData.startDate ?? null,
        endDate: siteVisitData.endDate ?? null
      };

      // Create new service with new site visit appended
      const newService = {
        ...service,
        siteVisits: [...(service.siteVisits || []), newSiteVisit]
      };

      await triggerUpdate(newService as MobileService);

      if (action === SubmitAction.AddAndClose) {
        setShowSiteVisitsModal(false);
      }
    } catch (error) {
      toast(CustomToastComponent, {
        toastId: "site-visit-add-error",
        data: {
          title: t("toasts:toasts.somethingwentwrong"),
          content: error.message,
          type: "error",
        }
      });
    }
  };

  const handleEditSiteVisit = async (siteVisitData: TSiteVisitSchema, currentSiteVisit: SiteVisit, setShowEditSiteVisitModal: Dispatch<SetStateAction<boolean>>) => {
    try {
      // Create a new service object with the updated site visit
      const newService = {
        ...service,
        siteVisits: service.siteVisits?.map((siteVisit: SiteVisit) =>
          siteVisit.id === currentSiteVisit.id ? { ...currentSiteVisit, ...siteVisitData } : siteVisit
        )
      };

      // Trigger the update with the new service object
      await triggerUpdate(newService as MobileService);

      toast(CustomToastComponent, {
        toastId: "site-visit-edit-success",
        data: {
          title: t("toasts:toasts.sitevisitupdatedsuccessfully"),
          type: "success",
        }
      });

      setShowEditSiteVisitModal(false);
    } catch (error) {
      toast(CustomToastComponent, {
        toastId: "site-visit-edit-error",
        data: {
          title: t("toasts:toasts.somethingwentwrong"),
          content: error.message,
          type: "error",
        }
      });
    }
  };

  const formatFieldValue = (fieldData, value) => {
    if (fieldData.format === "currency") {
      return formatNumberLocale(intl, value as number, "currency");
    } else if (fieldData.format === "boolean") {
      return t(value ? "common:yes" : "common:no");
    } else if (fieldData.format === "percent") {
      return `${value / 100}%`;
    } else {
      return value?.toString() + (fieldData.suffix ?? "");
    }
  };

  return (
    <div>
      <div className="flex-col-4">
        <div className="flex-wrap gap-4 flex mt-2">
          {inputFieldsData.map((fieldData) => (
            <TextInfoGroupAlt
              key={fieldData.name}
              label={fieldData.label}
              content={formatFieldValue(fieldData, service[fieldData.name])}
              className="min-w-[200px] max-w-[20%]"
            />
          ))}
        </div>

        {service.info && (
          <TextInfoGroupAlt
            label={t("mobileservice.inputs.info.label")}
            content={service.info}
          />
        )}

        {service.site && (
          <div className="flex flex-row gap-2 items-end mb-2">
            <TextInfoGroupAlt
              label={t("mobileservice.inputs.site.label")}
              content={`${service.site?.name} - ${appendSiteAddress(service.site as Site)}`}
              className="w-full md:min-w-[49%] md:flex-1"
            />
          </div>
        )}
      </div>

      {/* Site Visits */}
      <AddSiteVisitComponent
        userCanEdit={userCanEdit}
        onAddSiteVisit={handleAddSiteVisit}
        onEditSiteVisit={handleEditSiteVisit}
        siteVisitsToDisplay={service.siteVisits as SiteVisit[]}
        onRemoveSiteVisit={handleRemoveSiteVisit}
        setShowRemoveSiteVisitAlert={setShowRemoveSiteVisitAlert}
        showRemoveSiteVisitAlert={showRemoveSiteVisitAlert}
        editMode={true}
        isProcessing={isProcessing}
      />
    </div>
  );
};

export default EditMobileService;
